import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { AssetType } from '@modules/src/enums/AssetType'
import GameSignals from '../../signals/GameSignals'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { Orientation } from '@modules/src/enums/Orientation'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import Facade from '../../Facade'
import { ContainersNames } from '@/containers/ContainersNames'
import { startLogoAnimation } from '@/managers/ui/LogoManager'
import StateSignals from '@stateMachine/StateSignals'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import gsap from 'gsap'

let isFeatureIntroPopupVisible = false
//## True from the Hold & Win transition until holdAndWinStop — the window where the logo sits on
//## its base game placement even though the round is still free spins.
let isHoldAndWinPresentationActive = false

const shouldHideLogoForFeatureIntro = (): boolean => {
    const currentState = String((Facade as any).currentState || '')
    return currentState === GameStatesEnum.HOLD_AND_WIN_INTRO_STATE || isFeatureIntroPopupVisible
}

type LogoLayout = { x: number; y: number; scale: number }

//## Positions
const LOGO_LAYOUTS = new Map<Orientation, { baseGame: LogoLayout; freeSpins: LogoLayout }>([
    [
        Orientation.LANDSCAPE,
        {
            baseGame: { x: 199, y: 119.5, scale: 0.8 },
            freeSpins: { x: 1720, y: 170, scale: 0.6 },
        },
    ],
    [
        Orientation.PORTRAIT,
        {
            baseGame: { x: 558, y: 83.5, scale: 1.1 },
            freeSpins: { x: 205, y: -25, scale: 0.8 },
        },
    ],
])

const applyLogoLayout = (container: SpineAnimation): void => {
    const layouts = LOGO_LAYOUTS.get(ModulesFacade.orientation as Orientation)
    if (!layouts) {
        return
    }

    const isFreeSpins = Boolean(Facade.model?.roundState?.isFreeSpins)
    const layout = isFreeSpins && !isHoldAndWinPresentationActive ? layouts.freeSpins : layouts.baseGame

    container.position.set(layout.x, layout.y)
    container.scale.set(layout.scale, layout.scale)
}

//## Every show path routes through here, so placement and the flourish can never drift apart.
const showLogo = (container: SpineAnimation): void => {
    gsap.killTweensOf(container)
    container.alpha = 1
    applyLogoLayout(container)
    startLogoAnimation(container)
}

const FEATURE_INTRO_LOGO_FADE_OUT_S = 0.35
const FEATURE_INTRO_LOGO_FADE_IN_S = 0.25

const hideLogoForFeatureIntro = (container: SpineAnimation): void => {
    isFeatureIntroPopupVisible = true
    gsap.killTweensOf(container)

    if (!container.visible) {
        container.alpha = 1
        return
    }

    gsap.to(container, {
        duration: FEATURE_INTRO_LOGO_FADE_OUT_S,
        alpha: 0,
        onComplete: () => {
            container.visible = false
            container.alpha = 1
        },
    })
}

const revealLogoAfterFeatureIntro = (container: SpineAnimation): void => {
    isFeatureIntroPopupVisible = false

    container.visible = true
    showLogo(container)
    container.alpha = 0
    gsap.to(container, {
        duration: FEATURE_INTRO_LOGO_FADE_IN_S,
        alpha: 1,
    })
}

let isLogoRelayoutQueued = false

/**
 * ModulesManager emits gameResized before componentManager.updateLayout(), and that second pass
 * rewrites the spine with the base-game coordinates held in the component config — wrong while the
 * Free Game is running. Both run inside one synchronous task, so re-applying from a microtask lands
 * after updateLayout yet still before anything is rendered. Hiding the logo across the rotation is
 * therefore unnecessary, and doing so was visible as a blink because an orientation change drives
 * two resize passes (animation frame plus the settled timeout), hence two hide/show cycles.
 */
const scheduleLogoRelayout = (container: SpineAnimation): void => {
    if (isLogoRelayoutQueued) {
        return
    }

    isLogoRelayoutQueued = true
    queueMicrotask(() => {
        isLogoRelayoutQueued = false
        applyLogoLayout(container)
    })
}

export const LOGO_COMPONENT: IComponent = {
    name: ContainersNames.LOGO_CONTAINER,
    type: AssetType.CONTAINER,
    position: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 45,
            y: 27,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 0,
            y: 174,
        },
    ],
    items: [
        {
            name: 'Logo',
            type: AssetType.SPINE,
            visible: false,
            src: 'logoAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['idle'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        container.visible = Boolean(data.signalData)
                        if (container.visible) {
                            showLogo(container)
                        }
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, GameSignals.restoreFreeSpins],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        // Entering / restoring free spins while H&W may still be active. Free spins
                        // shows the logo in both orientations, so only the H&W intro suppresses it.
                        container.visible = !shouldHideLogoForFeatureIntro()

                        if (container.visible) {
                            showLogo(container)
                        }
                    },
                },
                {
                    onGameSignalsTriggered: [
                        (GameSignals as any).holdAndWinTransitionStart,
                        (GameSignals as any).holdAndWinStartSpin,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        isHoldAndWinPresentationActive = true
                        applyLogoLayout(data.component.componentElement as SpineAnimation)
                    },
                },
                {
                    // Dedicated handler: Component CUSTOM_NEW does not pass which signal fired,
                    // so holdAndWinStop cannot share a callback with FS background signals.
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        isHoldAndWinPresentationActive = false
                        isFeatureIntroPopupVisible = false
                        // After the H&W end popup the logo comes back for base game and free spins
                        // alike; applyLogoLayout picks the placement for whichever is running.
                        container.visible = true
                        showLogo(container)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        // Summary close can race with holdAndWinStop; ensure logo returns for base game.
                        if (Boolean(Facade.model?.roundState?.isFreeSpins) && !Boolean(Facade.model?.spinState?.isHoldAndWinActive)) {
                            container.visible = true
                            showLogo(container)
                            return
                        }

                        if (!Boolean(Facade.model?.roundState?.isFreeSpins)) {
                            isFeatureIntroPopupVisible = false
                            container.visible = true
                            showLogo(container)
                        }
                    },
                },
                {
                    onGameSignalsTriggered: [
                        GameSignals.showFreeSpinsIntro,
                        (GameSignals as any).featureIntroRequested,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        hideLogoForFeatureIntro(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        revealLogoAfterFeatureIntro(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [StateSignals.stateChanged as any],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        const stateId = String((data as any)?.signalData || '')

                        if (
                            stateId === GameStatesEnum.GET_SPIN_RESPONSE_STATE &&
                            Boolean(Facade.model?.spinState?.isHoldAndWinActive)
                        ) {
                            isFeatureIntroPopupVisible = false
                            container.visible = true
                            showLogo(container)
                            return
                        }

                        if (Boolean(Facade.model?.roundState?.isFreeSpins) && !Boolean(Facade.model?.spinState?.isHoldAndWinActive) && !shouldHideLogoForFeatureIntro()) {
                            container.visible = true
                            showLogo(container)
                        }
                    },
                },
                {
                    //## The gameResized signal is sent after the orientation change, so the logo can be repositioned
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        scheduleLogoRelayout(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        isFeatureIntroPopupVisible = false
                        container.visible = true
                        showLogo(container)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.startSpin],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        if (Facade.model.roundState.isFreeSpins) {
                            return
                        }
                        showLogo(container)
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 199,
                    y: 119.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 558,
                    y: 83.5,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.8,
                    y: 0.8,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1.1,
                    y: 1.1,
                },
            ],
        },
    ],
}
